import { Components, Theme } from '@mui/material';
import { ThemeMode } from '../mui';
import { designTokens } from '../themes/design-tokens';
import { RADIO_COLORS } from './radio.enum';

export const radioTheme: Components<Theme> = {
  MuiRadio: {
    styleOverrides: {
      root: ({ theme, ownerState }) => {
        const { palette } = theme;
        const isDark = palette.mode === ThemeMode.DARK;
        const tokens = designTokens.colors;

        const baseStyles = {
          padding: theme.spacing(1),
          borderRadius: '50%',
          transition: theme.transitions.create(
            ['background-color', 'box-shadow', 'border-color'],
            {
              duration: theme.transitions.duration.short,
              easing: theme.transitions.easing.easeInOut,
            },
          ),

          '&:hover': {
            backgroundColor: 'transparent',
            '&:before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              borderRadius: '50%',
              backgroundColor: palette.action.hover,
              transform: 'scale(1.2)',
              zIndex: -1,
            },
          },

          '&.Mui-focusVisible': {
            outline: 'none',
            boxShadow: `0 0 0 8px ${
              isDark
                ? `rgba(${palette.primary.main}, 0.2)`
                : `rgba(${palette.primary.main}, 0.15)`
            }`,
          },

          '&.Mui-disabled': {
            color: palette.action.disabled,
            cursor: 'not-allowed',

            '&:hover': {
              backgroundColor: 'transparent',
              '&:before': {
                display: 'none',
              },
            },
          },
        };

        const getColorStyles = () => {
          switch (ownerState.color) {
            case RADIO_COLORS.primary:
              return {
                color: palette.primary.main,
                '&.Mui-checked': {
                  color: palette.primary.main,
                },
                '&:hover': {
                  ...baseStyles['&:hover'],
                  backgroundColor: isDark
                    ? `rgba(${tokens.purple[500]}, 0.04)`
                    : `rgba(${tokens.purple[500]}, 0.04)`,
                },
              };

            case RADIO_COLORS.secondary:
              return {
                color: palette.secondary.main,
                '&.Mui-checked': {
                  color: palette.secondary.main,
                },
                '&:hover': {
                  ...baseStyles['&:hover'],
                  backgroundColor: isDark
                    ? `rgba(${tokens.lime[400]}, 0.04)`
                    : `rgba(${tokens.lime[400]}, 0.04)`,
                },
              };

            case RADIO_COLORS.success:
              return {
                color: palette.success.main,
                '&.Mui-checked': {
                  color: palette.success.main,
                },
                '&:hover': {
                  ...baseStyles['&:hover'],
                  backgroundColor: `rgba(${tokens.green[500]}, 0.04)`,
                },
              };

            case RADIO_COLORS.error:
              return {
                color: palette.error.main,
                '&.Mui-checked': {
                  color: palette.error.main,
                },
                '&:hover': {
                  ...baseStyles['&:hover'],
                  backgroundColor: `rgba(${tokens.red[500]}, 0.04)`,
                },
              };

            case RADIO_COLORS.warning:
              return {
                color: palette.warning.main,
                '&.Mui-checked': {
                  color: palette.warning.main,
                },
                '&:hover': {
                  ...baseStyles['&:hover'],
                  backgroundColor: `rgba(${tokens.yellow[500]}, 0.04)`,
                },
              };

            case RADIO_COLORS.info:
              return {
                color: palette.info.main,
                '&.Mui-checked': {
                  color: palette.info.main,
                },
                '&:hover': {
                  ...baseStyles['&:hover'],
                  backgroundColor: `rgba(${tokens.blue[500]}, 0.04)`,
                },
              };

            default:
              return {
                color: palette.action.active,
                '&.Mui-checked': {
                  color: palette.primary.main,
                },
              };
          }
        };

        const getSizeStyles = () => {
          switch (ownerState.size) {
            case 'small':
              return {
                padding: theme.spacing(0.5),
                '& .MuiSvgIcon-root': {
                  fontSize: '1.125rem',
                  width: '1.125rem',
                  height: '1.125rem',
                },
                width: '32px',
                height: '32px',
              };

            case 'medium':
              return {
                padding: theme.spacing(1),
                '& .MuiSvgIcon-root': {
                  fontSize: '1.5rem',
                  width: '1.5rem',
                  height: '1.5rem',
                },
                width: '42px',
                height: '42px',
              };

            default:
              return {
                padding: theme.spacing(1),
                '& .MuiSvgIcon-root': {
                  fontSize: '1.5rem',
                  width: '1.5rem',
                  height: '1.5rem',
                },
                width: '42px',
                height: '42px',
              };
          }
        };

        return {
          ...baseStyles,
          ...getColorStyles(),
          ...getSizeStyles(),
        };
      },
    },
  },

  MuiFormControlLabel: {
    styleOverrides: {
      root: ({ theme }) => ({
        margin: 0,
        marginRight: theme.spacing(2),

        '&:last-child': {
          marginRight: 0,
        },
      }),

      label: ({ theme, ownerState }) => {
        const getSizeLabelStyles = () => {
          const controlProps = ownerState?.control?.props;
          if (
            controlProps &&
            typeof controlProps === 'object' &&
            controlProps !== null &&
            'size' in controlProps
          ) {
            switch (controlProps.size) {
              case 'small':
                return {
                  fontSize: theme.typography.body2.fontSize,
                  lineHeight: theme.typography.body2.lineHeight,
                };
              default:
                return {
                  fontSize: theme.typography.body1.fontSize,
                  lineHeight: theme.typography.body1.lineHeight,
                };
            }
          }
          return {
            fontSize: theme.typography.body1.fontSize,
            lineHeight: theme.typography.body1.lineHeight,
          };
        };

        return {
          color: theme.palette.text.primary,
          marginLeft: theme.spacing(1),
          userSelect: 'none',
          ...getSizeLabelStyles(),

          '&.Mui-disabled': {
            color: theme.palette.text.disabled,
          },
        };
      },
    },
  },

  MuiRadioGroup: {
    styleOverrides: {
      root: ({ theme }) => ({
        '& .MuiFormControlLabel-root': {
          marginBottom: theme.spacing(0.5),

          '&:last-child': {
            marginBottom: 0,
          },
        },
      }),
    },
  },
};
